import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { LINK, fmt } from '../lib/hiring';

// @@JOB_SHARE_v111@@ Share & print for a job posting: copy links, QR code with a
// scan/click counter, and the US Letter flyer with tear-off tabs.

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch (e) { /* fall back below */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (e) { return false; }
}

const SMALL = { width: 'auto', padding: '0 12px', marginTop: 0, height: 30, fontSize: 12, whiteSpace: 'nowrap' };

export function CopyButton({ text, label = 'Copy', style }) {
  const [state, setState] = useState('');
  async function go(e) {
    e.preventDefault(); e.stopPropagation();
    const ok = await copyText(text);
    setState(ok ? 'Copied!' : 'Copy failed');
    setTimeout(() => setState(''), 1800);
  }
  return (
    <button type="button" className="hq-signout" style={{ ...SMALL, ...style }} onClick={go} aria-label={label + ' ' + text}>
      {state || label}
    </button>
  );
}

async function download(path, filename) {
  const res = await apiFetch(path);
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.error || 'Download failed.');
  }
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

const S = {
  body: { padding: 20, display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' },
  qrBox: { background: '#fff', borderRadius: 10, padding: 8, width: 168, height: 168, boxSizing: 'border-box', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  col: { flex: '1 1 300px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 },
  row: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  label: { fontSize: 11, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.06em', width: 82, flex: '0 0 auto' },
  url: { fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: '0 1 auto' },
  stats: { display: 'flex', gap: 22, flexWrap: 'wrap' },
  num: { fontSize: 26, fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  numLabel: { fontSize: 11, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.06em' },
  muted: { fontSize: 12, color: 'var(--hq-muted)' },
};

function Bars({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.n));
  return (
    <div title="Scans per day, last 14 days" style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 34 }}>
      {daily.map((d) => (
        <div key={d.day} title={`${d.day}: ${d.n}`}
          style={{ width: 10, height: Math.max(2, Math.round((d.n / max) * 34)), borderRadius: 2, background: d.n ? '#31b44b' : 'rgba(255,255,255,.12)' }} />
      ))}
    </div>
  );
}

// postingId + slug/status (re-fetch when the URL or status changes after a save).
export function ShareCard({ postingId, slug, status }) {
  const [s, setS] = useState(null);
  const [qr, setQr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  async function load() {
    const res = await apiFetch('/api/jobs/postings/' + postingId + '/share');
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.error || 'Could not load the share links.'); return; }
    setMsg(''); setS(d);
  }
  useEffect(() => { load(); }, [postingId, slug, status]);

  useEffect(() => { // QR preview needs the Bearer header -> blob URL
    if (!s) return undefined;
    let url = ''; let live = true;
    apiFetch('/api/jobs/postings/' + postingId + '/qr.png?size=480')
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => { if (!live) return; url = URL.createObjectURL(b); setQr(url); })
      .catch(() => {});
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [postingId, s && s.short_code]);

  async function dl(kind) {
    setBusy(kind); setMsg('');
    try {
      const base = 'xyz-job-' + slug;
      if (kind === 'png') await download('/api/jobs/postings/' + postingId + '/qr.png?size=1500&download=1', base + '-qr.png');
      if (kind === 'svg') await download('/api/jobs/postings/' + postingId + '/qr.svg?download=1', base + '-qr.svg');
      if (kind === 'flyer') await download('/api/jobs/postings/' + postingId + '/flyer.pdf', base + '-flyer.pdf');
    } catch (e) { setMsg(e.message); } finally { setBusy(''); }
  }

  const st = s && s.stats;
  return (
    <div className="hq-panel" style={{ marginBottom: 16 }}>
      <div className="hq-panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <h2>Share &amp; print</h2>
        {s && <button type="button" className="hq-signout" style={SMALL} onClick={load}>Refresh count</button>}
      </div>
      {!s ? <div style={{ padding: 20 }} className="hq-msg">{msg || 'Loading…'}</div> : (
        <div style={S.body}>
          <div style={S.qrBox}>
            {qr ? <img src={qr} alt={'QR code for ' + s.short_display} style={{ width: '100%', height: '100%', imageRendering: 'pixelated' }} />
              : <span style={{ color: '#888', fontSize: 12 }}>QR…</span>}
          </div>
          <div style={S.col}>
            {status !== 'open' && (
              <div className="hq-msg" style={{ color: '#e8c15a', margin: 0 }}>
                {status === 'draft'
                  ? 'Draft — the QR code and short link go to the careers list until you open this posting. You can print flyers now.'
                  : 'Closed — the QR code and short link show “Position filled”.'}
              </div>
            )}
            <div style={S.row}>
              <span style={S.label}>Job page</span>
              <a href={s.public_url} target="_blank" rel="noopener noreferrer" style={{ ...LINK, ...S.url }}>{s.public_url.replace(/^https?:\/\//, '')}</a>
              <CopyButton text={s.public_url} />
            </div>
            <div style={S.row}>
              <span style={S.label}>Short link</span>
              <a href={s.short_url} target="_blank" rel="noopener noreferrer" style={{ ...LINK, ...S.url }}>{s.short_display}</a>
              <CopyButton text={s.short_url} />
            </div>
            <div style={S.muted}>The short link, QR code and flyer keep working if you change the page URL. Every scan or click is counted (bots excluded).</div>

            <div style={{ ...S.row, alignItems: 'flex-end', gap: 26 }}>
              <div style={S.stats}>
                <div><div style={S.num}>{st.total}</div><div style={S.numLabel}>Scans &amp; clicks</div></div>
                <div title="One device counted once per day"><div style={S.num}>{st.unique}</div><div style={S.numLabel}>Unique</div></div>
                <div><div style={S.num}>{st.last7}</div><div style={S.numLabel}>Last 7 days</div></div>
              </div>
              <Bars daily={st.daily} />
            </div>
            <div style={S.muted}>
              Flyer QR {st.by_source.flyer} · Downloaded QR {st.by_source.qr} · Short link {st.by_source.link}
              {st.last_at ? <> · last {fmt(st.last_at)}</> : null}
            </div>

            <div style={{ ...S.row, gap: 8 }}>
              <button type="button" className="hq-btn" style={{ width: 'auto', marginTop: 0 }} disabled={!!busy || status === 'closed'}
                title={status === 'closed' ? 'Reopen the posting to print flyers' : 'US Letter with tear-off tabs'} onClick={() => dl('flyer')}>
                {busy === 'flyer' ? 'Building…' : 'Download flyer (PDF)'}
              </button>
              <button type="button" className="hq-signout" style={{ ...SMALL, height: 'auto', minHeight: 36 }} disabled={!!busy} onClick={() => dl('png')}>{busy === 'png' ? '…' : 'QR PNG'}</button>
              <button type="button" className="hq-signout" style={{ ...SMALL, height: 'auto', minHeight: 36 }} disabled={!!busy} onClick={() => dl('svg')}>{busy === 'svg' ? '…' : 'QR SVG'}</button>
            </div>
            {msg && <div className="hq-msg" style={{ margin: 0 }}>{msg}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
